// ---------------------------------------------------------------------------
// Escala vertical das velas visíveis (controlador puro, sem React e sem Three)
//
// Lugar único do estado de escala do cubo. Os componentes leem o domínio desenhado
// (dMin..dMax) por ref, a cada quadro. Hoje só existe o modo 'auto'; um modo manual
// compartilhado (janelas 2D, botão "redefinir escalas") pode entrar depois pela
// mesma interface sem mudar quem consome.
//
// Política 'auto' (decisão do CEO, Item 2):
//  - alvo = máxima/mínima das velas VISÍVEIS (pavios) e das SMAs ativas, + margem de 8%;
//  - antecipação: inclui as velas do overscan que vão entrar no sentido do movimento;
//  - abre com τ 100 ms e fecha com τ 300 ms (histerese), sempre por tempo (dt), não por quadro;
//  - taxa máxima de variação: 3,0 faixas por segundo (= 5% por quadro a 60 FPS);
//  - garantia: nunca corta vela visível (encosta nela com 2% de folga).
// Sem alocação por chamada: só números e o objeto do próprio controlador.
// ---------------------------------------------------------------------------

export type ScaleMode = 'auto';

export interface VisibleScaleParams {
  padFrac: number;          // margem sobre a faixa do alvo (cada lado)
  tauOpenMs: number;        // constante de tempo para abrir a faixa
  tauCloseMs: number;       // constante de tempo para fechar a faixa
  maxRatePerSec: number;    // variação máxima de cada borda, em faixas por segundo
  hardMarginFrac: number;   // folga mínima garantida acima/abaixo das velas visíveis
  lookaheadFactor: number;  // velas antecipadas = |velocidade| × τ_abrir × fator
  maxLookahead: number;     // limite da antecipação (velas; o overscan tem 500)
  maxDtMs: number;          // dt máximo considerado (aba em segundo plano, travadas)
}

export const DEFAULT_SCALE_PARAMS: VisibleScaleParams = {
  padFrac: 0.08,
  tauOpenMs: 100,
  tauCloseMs: 300,
  maxRatePerSec: 3.0,
  hardMarginFrac: 0.02,
  lookaheadFactor: 3,
  maxLookahead: 500,
  maxDtMs: 100,
};

/** Campos lidos de cada vela (os mesmos de DataPoint). */
export interface ScaleCandle {
  high?: number; low?: number; price?: number; close?: number;
  sma10?: number; sma20?: number; sma50?: number;
}

export interface ScaleSmaFlags { sma10: boolean; sma20: boolean; sma50: boolean }

export class VisibleScaleController {
  mode: ScaleMode = 'auto';
  readonly params: VisibleScaleParams;
  /** Domínio de preço desenhado (o que as paredes mostram de baixo a cima). */
  dMin = NaN;
  dMax = NaN;
  /** Faixa das velas visíveis no último update (pavios e SMAs ativas). */
  vMin = NaN;
  vMax = NaN;
  private snapNext = true;
  // resultado do último scan (evita alocar tuplas)
  private sMin = Infinity;
  private sMax = -Infinity;
  // antecipação de cada lado (velas), decai com τ_fechar: numa inversão do arrasto o lado de onde
  // se vinha continua coberto por um instante, em vez de sumir de uma vez
  private lookPast = 0;
  private lookPresent = 0;

  constructor(params: Partial<VisibleScaleParams> = {}) {
    this.params = { ...DEFAULT_SCALE_PARAMS, ...params };
  }

  /** O próximo update salta direto para o alvo (troca de ativo/timeframe, primeira carga). */
  reset(): void { this.snapNext = true; this.lookPast = 0; this.lookPresent = 0; }

  get ready(): boolean { return this.dMax > this.dMin; }

  private scan(data: ArrayLike<ScaleCandle>, a: number, b: number, sma: ScaleSmaFlags): void {
    let mn = Infinity, mx = -Infinity;
    const lo = Math.max(0, a), hi = Math.min(data.length - 1, b);
    for (let i = lo; i <= hi; i++) {
      const d = data[i];
      const h = d.high ?? d.close ?? d.price, l = d.low ?? d.close ?? d.price;
      if (h !== undefined && h === h && h > mx) mx = h;
      if (l !== undefined && l === l && l < mn) mn = l;
      if (sma.sma10) { const v = d.sma10; if (v !== undefined && v === v) { if (v < mn) mn = v; if (v > mx) mx = v; } }
      if (sma.sma20) { const v = d.sma20; if (v !== undefined && v === v) { if (v < mn) mn = v; if (v > mx) mx = v; } }
      if (sma.sma50) { const v = d.sma50; if (v !== undefined && v === v) { if (v < mn) mn = v; if (v > mx) mx = v; } }
    }
    this.sMin = mn; this.sMax = mx;
  }

  /**
   * Atualiza o domínio desenhado.
   * @param data       velas do lote (índices do lote)
   * @param visStart   primeiro índice visível (inclusive)
   * @param visEnd     último índice visível (inclusive)
   * @param velocity   velocidade do offset em velas/s (positivo = indo ao passado, novas velas entram pela esquerda)
   * @param dtMs       tempo desde o update anterior
   */
  update(data: ArrayLike<ScaleCandle>, visStart: number, visEnd: number, velocity: number, dtMs: number, sma: ScaleSmaFlags): void {
    const P = this.params;
    if (data.length === 0 || visEnd < visStart) return;
    this.scan(data, visStart, visEnd, sma);
    let vMin = this.sMin, vMax = this.sMax;
    if (!(vMax >= vMin)) return;
    this.vMin = vMin; this.vMax = vMax;
    let vr = vMax - vMin;
    if (vr <= 0) vr = Math.max(Math.abs(vMax) * 1e-3, 1e-9);

    // Antecipação: velas do overscan que vão entrar no sentido do movimento
    let aMin = vMin, aMax = vMax;
    const decay = Math.exp(-Math.min(P.maxDtMs, Math.max(0, dtMs)) / P.tauCloseMs);
    const k = Math.min(P.maxLookahead, Math.abs(velocity) * (P.tauOpenMs / 1000) * P.lookaheadFactor);
    this.lookPast *= decay; this.lookPresent *= decay;
    if (velocity > 0 && k > this.lookPast) this.lookPast = k;
    if (velocity < 0 && k > this.lookPresent) this.lookPresent = k;
    const kPast = Math.ceil(this.lookPast - 0.5), kPresent = Math.ceil(this.lookPresent - 0.5);
    if (kPast > 0) {
      this.scan(data, visStart - kPast, visStart - 1, sma);
      if (this.sMin < aMin) aMin = this.sMin;
      if (this.sMax > aMax) aMax = this.sMax;
    }
    if (kPresent > 0) {
      this.scan(data, visEnd + 1, visEnd + kPresent, sma);
      if (this.sMin < aMin) aMin = this.sMin;
      if (this.sMax > aMax) aMax = this.sMax;
    }
    const ar = Math.max(aMax - aMin, vr);
    const tMin = aMin - P.padFrac * ar;
    const tMax = aMax + P.padFrac * ar;

    if (this.snapNext || !(this.dMax > this.dMin)) {
      this.dMin = tMin; this.dMax = tMax; this.snapNext = false;
      return;
    }

    const dt = Math.min(P.maxDtMs, Math.max(0, dtMs));
    const aOpen = 1 - Math.exp(-dt / P.tauOpenMs);
    const aClose = 1 - Math.exp(-dt / P.tauCloseMs);
    const R0 = this.dMax - this.dMin;
    const cap = P.maxRatePerSec * R0 * (dt / 1000);

    let nMax = this.dMax + (tMax - this.dMax) * (tMax > this.dMax ? aOpen : aClose);
    let nMin = this.dMin + (tMin - this.dMin) * (tMin < this.dMin ? aOpen : aClose);
    if (nMax > this.dMax + cap) nMax = this.dMax + cap; else if (nMax < this.dMax - cap) nMax = this.dMax - cap;
    if (nMin > this.dMin + cap) nMin = this.dMin + cap; else if (nMin < this.dMin - cap) nMin = this.dMin - cap;

    // Garantia: nenhuma vela visível fica fora do domínio desenhado
    const hm = P.hardMarginFrac * vr;
    if (vMax + hm > nMax) nMax = vMax + hm;
    if (vMin - hm < nMin) nMin = vMin - hm;

    this.dMin = nMin; this.dMax = nMax;
  }
}

/**
 * Transformação afim em Y que leva a geometria desenhada com um domínio-base (bMin..bMax)
 * para o domínio desenhado (dMin..dMax), ambos mapeados em [-H/2, H/2].
 * Escreve em out[0] = escala, out[1] = deslocamento: y_desenhado = escala * y_base + deslocamento.
 */
export function yAffine(bMin: number, bMax: number, dMin: number, dMax: number, H: number, out: Float64Array | number[]): void {
  const bR = bMax - bMin, dR = dMax - dMin;
  if (!(bR > 0) || !(dR > 0)) { out[0] = 1; out[1] = 0; return; }
  const a = bR / dR;
  out[0] = a;
  out[1] = ((bMin - dMin) / dR) * H - H / 2 + a * (H / 2);
}

/** Preço -> Y no domínio desenhado. */
export function priceToY(price: number, dMin: number, dMax: number, H: number): number {
  return ((price - dMin) / (dMax - dMin)) * H - H / 2;
}

/** Y -> preço no domínio desenhado. */
export function yToPrice(y: number, dMin: number, dMax: number, H: number): number {
  return ((y + H / 2) / H) * (dMax - dMin) + dMin;
}

/**
 * Índices do lote que aparecem na janela visível, dado o deslocamento do grupo (computeSliceShift).
 * O grupo anda shift velas para a direita; a vela de índice i fica em x = início + (i - padLeft + shift) passos.
 */
export function visibleBatchRange(padLeft: number, shift: number, visibleCount: number, length: number, out: Int32Array | number[]): void {
  out[0] = Math.max(0, Math.ceil(padLeft - shift - 1e-9));
  out[1] = Math.min(length - 1, Math.floor(padLeft - shift + visibleCount - 1 + 1e-9));
}

// ---------------------------------------------------------------------------
// Profundidade Z do filamento (momentum), desacoplada do preço (decisão do CEO, Item 2).
//
// zScale desenhado = (1 − folga) × zLimit / maior |momentum| visível (inclui as SMAs ativas com o
// mesmo amortecimento do desenho). Usa o MÁXIMO, não o p99,5: só assim o filamento cabe sempre
// dentro das bordas do cubo com folga. Mesma antecipação do preço; quando precisa encolher, encolhe
// com τ 100 ms e, se algo visível passaria da folga, encolhe na hora (garantia); cresce com τ 300 ms.
// Taxa máxima 3,0 por segundo (relativa). Tudo por dt, sem alocação por update.
// ---------------------------------------------------------------------------

export interface DepthCandle {
  momentum?: number;
  sma10Momentum?: number; sma20Momentum?: number; sma50Momentum?: number;
}

/** Peso de cada SMA no Z desenhado (0 = desligada). Igual ao amortecimento usado no desenho. */
export interface DepthSmaWeights { sma10: number; sma20: number; sma50: number }

export interface VisibleDepthParams {
  marginFrac: number;       // folga mínima até a borda do cubo
  tauShrinkMs: number;
  tauGrowMs: number;
  maxRatePerSec: number;
  lookaheadFactor: number;
  maxLookahead: number;
  maxDtMs: number;
}

export const DEFAULT_DEPTH_PARAMS: VisibleDepthParams = {
  marginFrac: 0.05,
  tauShrinkMs: 100,
  tauGrowMs: 300,
  maxRatePerSec: 3.0,
  lookaheadFactor: 3,
  maxLookahead: 500,
  maxDtMs: 100,
};

export class VisibleDepthController {
  readonly params: VisibleDepthParams;
  /** Z do mundo por unidade de momentum (o que o desenho usa). */
  zScale = NaN;
  /** Maior |momentum| ponderado visível no último update. */
  vMaxAbs = NaN;
  private snapNext = true;
  private lookPast = 0;
  private lookPresent = 0;

  constructor(params: Partial<VisibleDepthParams> = {}) {
    this.params = { ...DEFAULT_DEPTH_PARAMS, ...params };
  }

  reset(): void { this.snapNext = true; this.lookPast = 0; this.lookPresent = 0; }
  get ready(): boolean { return this.zScale > 0; }

  private maxAbs(data: ArrayLike<DepthCandle>, a: number, b: number, w: DepthSmaWeights): number {
    let m = 0;
    const lo = Math.max(0, a), hi = Math.min(data.length - 1, b);
    for (let i = lo; i <= hi; i++) {
      const d = data[i];
      let v = d.momentum; if (v !== undefined && v === v) { v = Math.abs(v); if (v > m) m = v; }
      if (w.sma10 > 0) { const x = d.sma10Momentum; if (x !== undefined && x === x) { const y = Math.abs(x) * w.sma10; if (y > m) m = y; } }
      if (w.sma20 > 0) { const x = d.sma20Momentum; if (x !== undefined && x === x) { const y = Math.abs(x) * w.sma20; if (y > m) m = y; } }
      if (w.sma50 > 0) { const x = d.sma50Momentum; if (x !== undefined && x === x) { const y = Math.abs(x) * w.sma50; if (y > m) m = y; } }
    }
    return m;
  }

  /**
   * @param zLimit  meia-profundidade do cubo (as bordas ficam em ±zLimit)
   * Os demais parâmetros têm o mesmo sentido de VisibleScaleController.update.
   */
  update(data: ArrayLike<DepthCandle>, visStart: number, visEnd: number, velocity: number, dtMs: number, w: DepthSmaWeights, zLimit: number): void {
    const P = this.params;
    if (data.length === 0 || visEnd < visStart || !(zLimit > 0)) return;
    const vMax = this.maxAbs(data, visStart, visEnd, w);
    this.vMaxAbs = vMax;
    const dt = Math.min(P.maxDtMs, Math.max(0, dtMs));
    const decay = Math.exp(-dt / P.tauGrowMs);
    const k = Math.min(P.maxLookahead, Math.abs(velocity) * (P.tauShrinkMs / 1000) * P.lookaheadFactor);
    this.lookPast *= decay; this.lookPresent *= decay;
    if (velocity > 0 && k > this.lookPast) this.lookPast = k;
    if (velocity < 0 && k > this.lookPresent) this.lookPresent = k;
    let aMax = vMax;
    const kPast = Math.ceil(this.lookPast - 0.5), kPresent = Math.ceil(this.lookPresent - 0.5);
    if (kPast > 0) { const m = this.maxAbs(data, visStart - kPast, visStart - 1, w); if (m > aMax) aMax = m; }
    if (kPresent > 0) { const m = this.maxAbs(data, visEnd + 1, visEnd + kPresent, w); if (m > aMax) aMax = m; }

    const usable = (1 - P.marginFrac) * zLimit;
    const target = aMax > 0 ? usable / aMax : (this.zScale > 0 ? this.zScale : 1);
    const hardMax = vMax > 0 ? usable / vMax : Infinity; // acima disso algo visível passaria da folga

    if (this.snapNext || !(this.zScale > 0)) {
      this.zScale = Math.min(target, hardMax); this.snapNext = false;
      return;
    }
    const cur = this.zScale;
    const a = 1 - Math.exp(-dt / (target < cur ? P.tauShrinkMs : P.tauGrowMs));
    let next = cur + (target - cur) * a;
    const cap = P.maxRatePerSec * cur * (dt / 1000);
    if (next > cur + cap) next = cur + cap; else if (next < cur - cap) next = cur - cap;
    if (next > hardMax) next = hardMax; // garantia: nunca sai do cubo
    this.zScale = next;
  }
}
